(() => {
  'use strict';

  const SELECTOR = '[data-cut-builder-v2]';

  class CustomCutCalculatorV2 {
    constructor(builder) {
      this.builder = builder;
      this.sectionId = builder.dataset.sectionId;
      this.productInfo = builder.closest('product-info');
      this.form = document.getElementById(builder.dataset.productFormId);
      this.configElement = document.getElementById(`cut-pricing-config-v2-${this.sectionId}`);
      this.rowsElement = builder.querySelector('[data-cut-rows-v2]');
      this.template = document.getElementById(`CustomCutRowV2-${this.sectionId}`);
      this.messageElement = builder.querySelector('[data-cut-message-v2]');
      this.propertiesContainer = this.form?.querySelector('[data-v2-dynamic-properties]');
      this.materialElement = builder.querySelector('[data-material-total-v2]');
      this.cutChargeElement = builder.querySelector('[data-cut-charge-v2]');
      this.totalElement = builder.querySelector('[data-config-total-v2]');
      this.mainTotalElement = this.productInfo?.querySelector('#totalPriceV2');
      this.config = this.readConfig();
      this.lastResult = null;
      this.unsubscribeVariant = null;

      if (!this.rowsElement || !this.template || !this.form || !this.configElement) {
        console.error('V2 custom-cut calculator could not initialize because required markup is missing.');
        return;
      }

      this.bindEvents();
      this.addRow();
      this.observeVariantPrice();
      this.subscribeToVariantChanges();
      this.calculate({ validate: false });
      builder.dataset.initialized = 'true';
    }

    readConfig() {
      const el = this.configElement;
      const number = (value, fallback = 0) => {
        const parsed = Number.parseFloat(value);
        return Number.isFinite(parsed) ? parsed : fallback;
      };

      const roundSet = String(el?.dataset.roundSet || '')
        .split(',')
        .map((value) => number(value, NaN))
        .filter(Number.isFinite)
        .sort((a, b) => a - b);

      return {
        roundSet,
        maxLength: number(el?.dataset.maxL, 96),
        maxWidth: number(el?.dataset.maxW, 48),
        roundingOffset: number(el?.dataset.roundingOffset, 0.124),
        cutRates: {
          1: number(el?.dataset.cutPrice1, 10),
          2: number(el?.dataset.cutPrice2, 5),
          3: number(el?.dataset.cutPrice3, 4),
          4: number(el?.dataset.cutPrice4, 3),
          5: number(el?.dataset.cutPrice5, 2.5),
          sixPlus: number(el?.dataset.cutPrice6Plus, 2),
        },
      };
    }

    bindEvents() {
      this.builder.querySelector('[data-add-cut-v2]')?.addEventListener('click', () => this.addRow());
      this.builder.querySelector('[data-calculate-cuts-v2]')?.addEventListener('click', () => {
        this.calculate({ validate: true, focusError: true });
      });

      this.rowsElement.addEventListener('click', (event) => {
        const button = event.target.closest('[data-remove-cut-v2]');
        if (!button) return;
        const row = button.closest('[data-cut-row-v2]');
        if (!row) return;

        if (this.rowsElement.querySelectorAll('[data-cut-row-v2]').length === 1) {
          this.resetRow(row);
        } else {
          row.remove();
        }
        this.renumberRows();
        this.calculate({ validate: false });
      });

      const recalculate = () => this.calculate({ validate: false });
      this.rowsElement.addEventListener('input', recalculate);
      this.rowsElement.addEventListener('change', recalculate);

      this.form.addEventListener('submit', (event) => {
        const result = this.calculate({ validate: true, focusError: true });
        if (!result) {
          event.preventDefault();
          event.stopImmediatePropagation();
        }
      }, true);

      this.productInfo?.querySelector('.qty__input')?.addEventListener('input', recalculate);
      this.productInfo?.querySelector('.qty__input')?.addEventListener('change', recalculate);

      const clearButton = this.productInfo?.querySelector('[data-cut-clear-v2]');
      clearButton?.addEventListener('click', () => this.clearAll());

      this.productInfo?.addEventListener('change', (event) => {
        if (event.target.closest('variant-selects') && !event.target.closest('[data-cut-builder-v2]')) {
          window.setTimeout(() => this.calculate({ validate: false }), 80);
        }
      });
    }

    subscribeToVariantChanges() {
      try {
        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.variantChange) {
          this.unsubscribeVariant = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
            if (!data || data.sectionId === this.sectionId) {
              window.setTimeout(() => this.calculate({ validate: false }), 0);
            }
          });
        }
      } catch (error) {
        console.warn('V2 calculator could not subscribe to variant changes.', error);
      }
    }

    observeVariantPrice() {
      const priceContainer = this.productInfo?.querySelector(`#price-${CSS.escape(this.sectionId)}`);
      if (!priceContainer || !('MutationObserver' in window)) return;

      this.priceObserver = new MutationObserver(() => this.calculate({ validate: false }));
      this.priceObserver.observe(priceContainer, { childList: true, subtree: true, attributes: true });
    }

    addRow(values = {}) {
      const fragment = this.template.content.cloneNode(true);
      const row = fragment.querySelector('[data-cut-row-v2]');
      if (!row) return;

      row.querySelector('[data-cut-width-v2]').value = values.width ?? '';
      row.querySelector('[data-cut-length-v2]').value = values.length ?? '';
      row.querySelector('[data-cut-pieces-v2]').value = values.pieces ?? 1;
      row.querySelector('[data-cut-tolerance-v2]').value = values.tolerance ?? '';
      this.rowsElement.appendChild(fragment);
      this.renumberRows();
    }

    resetRow(row) {
      row.querySelector('[data-cut-width-v2]').value = '';
      row.querySelector('[data-cut-length-v2]').value = '';
      row.querySelector('[data-cut-pieces-v2]').value = '1';
      row.querySelector('[data-cut-tolerance-v2]').value = '';
      row.querySelectorAll('.input-error').forEach((input) => input.classList.remove('input-error'));
    }

    clearAll() {
      this.rowsElement.innerHTML = '';
      this.addRow();
      this.hideMessage();
      this.calculate({ validate: false });
    }

    renumberRows() {
      this.rowsElement.querySelectorAll('[data-cut-row-v2]').forEach((row, index) => {
        row.dataset.cutIndex = String(index + 1);
        const removeButton = row.querySelector('[data-remove-cut-v2]');
        if (removeButton) removeButton.setAttribute('aria-label', `Remove cut ${index + 1}`);
      });
    }

    getUnitPrice() {
      const visiblePrice = this.productInfo?.querySelector('.main_var_price[data-price]');
      const cents = Number(visiblePrice?.dataset.price);
      if (Number.isFinite(cents) && cents >= 0) return cents / 100;

      const selectedVariant = this.productInfo?.querySelector('variant-selects [data-selected-variant]');
      if (selectedVariant?.textContent.trim()) {
        try {
          const variant = JSON.parse(selectedVariant.textContent);
          if (Number.isFinite(variant?.price)) return variant.price / 100;
        } catch (_) {}
      }
      return 0;
    }

    roundUpToSet(value, cap) {
      let number = Number.parseFloat(value);
      if (!Number.isFinite(number) || number <= 0) return 0;
      number = Math.min(number, cap);

      if (!this.config.roundSet.length) return number;
      for (const size of this.config.roundSet) {
        if (size > cap) break;
        if (number <= size - this.config.roundingOffset - 1e-9) return size;
      }

      const available = this.config.roundSet.filter((size) => size <= cap);
      return available.at(-1) || number;
    }

    getCutRate(pieces) {
      if (pieces >= 6) return this.config.cutRates.sixPlus;
      return this.config.cutRates[pieces] ?? this.config.cutRates.sixPlus;
    }

    readRows({ validate = false } = {}) {
      const rows = [...this.rowsElement.querySelectorAll('[data-cut-row-v2]')];
      let firstInvalid = null;
      const errors = [];

      const cuts = rows.map((row, index) => {
        const widthInput = row.querySelector('[data-cut-width-v2]');
        const lengthInput = row.querySelector('[data-cut-length-v2]');
        const piecesInput = row.querySelector('[data-cut-pieces-v2]');
        const toleranceInput = row.querySelector('[data-cut-tolerance-v2]');
        const width = Number.parseFloat(widthInput.value);
        const length = Number.parseFloat(lengthInput.value);
        const pieces = Number.parseInt(piecesInput.value, 10);
        const tolerance = toleranceInput.value.trim();

        [widthInput, lengthInput, piecesInput, toleranceInput].forEach((input) => input.classList.remove('input-error'));

        const mark = (input, message) => {
          if (!validate) return;
          input.classList.add('input-error');
          errors.push(`Cut ${index + 1}: ${message}`);
          firstInvalid ||= input;
        };

        if (!Number.isFinite(width) || width <= 0) mark(widthInput, 'enter a valid width.');
        else if (width > this.config.maxWidth) mark(widthInput, `maximum width is ${this.config.maxWidth} inches.`);

        if (!Number.isFinite(length) || length <= 0) mark(lengthInput, 'enter a valid length.');
        else if (length > this.config.maxLength) mark(lengthInput, `maximum length is ${this.config.maxLength} inches.`);

        if (!Number.isInteger(pieces) || pieces < 1) mark(piecesInput, 'pieces must be a whole number of 1 or more.');
        if (!tolerance) mark(toleranceInput, 'select a tolerance.');

        return { number: index + 1, width, length, pieces, tolerance };
      });

      return { cuts, errors, firstInvalid };
    }

    syncB2BDiscountTableForVariant() {
      const tableBody = this.productInfo?.querySelector('tbody[data-company_gid]');
      if (!tableBody) return;

      const companyId = tableBody.querySelector('.table-price-b2b')?.dataset.companyid || '';
      const variantId = String(this.productInfo?.querySelector('.product-variant-id')?.value || '');
      const entries = [...this.productInfo.querySelectorAll('.bulk_discount_entry')];
      const entry = entries.find((item) => item.dataset.company === companyId && item.dataset.variant === variantId);

      const defaults = {
        1: Number.parseFloat(tableBody.getAttribute('data-def-1-5')) || 0,
        2: Number.parseFloat(tableBody.getAttribute('data-def-6-10')) || 0,
        3: Number.parseFloat(tableBody.getAttribute('data-def-11-20')) || 0,
        4: Number.parseFloat(tableBody.getAttribute('data-def-20-plus')) || 0,
      };

      const discounts = entry ? {
        1: Number.parseFloat(entry.dataset.discount_for_1_to_5) || 0,
        2: Number.parseFloat(entry.dataset.discount_for_6_to_10) || 0,
        3: Number.parseFloat(entry.dataset.discount_for_11_to_20) || 0,
        4: Number.parseFloat(entry.dataset.discount_for_20) || 0,
      } : defaults;

      Object.entries(discounts).forEach(([position, percent]) => {
        const row = tableBody.querySelector(`.discount-table-row[data-pos="${position}"]`);
        const priceCell = row?.querySelector('.table-price-b2b');
        const savingsCell = row?.querySelector('.savings');
        if (priceCell) priceCell.dataset.off = String(percent);
        if (savingsCell) savingsCell.textContent = `(${percent}%)`;
      });
    }

    getDiscount(total) {
      this.syncB2BDiscountTableForVariant();
      const tableBody = this.productInfo?.querySelector('tbody[data-company_gid]');
      if (!tableBody) return { amount: total, percent: 0 };

      const quantity = Number.parseInt(this.productInfo.querySelector('.qty__input')?.value, 10) || 1;
      let position = 1;
      if (quantity > 5 && quantity <= 10) position = 2;
      else if (quantity > 10 && quantity < 20) position = 3;
      else if (quantity >= 20) position = 4;

      tableBody.querySelectorAll('.discount-table-row').forEach((row) => row.classList.remove('selected'));
      tableBody.querySelector(`.discount-table-row[data-pos="${position}"]`)?.classList.add('selected');

      tableBody.querySelectorAll('.discount-table-row').forEach((row) => {
        const percent = Number.parseFloat(row.querySelector('.table-price-b2b')?.dataset.off) || 0;
        const price = total - (total * percent / 100);
        const priceCell = row.querySelector('.table-price-b2b');
        if (priceCell) priceCell.textContent = this.formatMoney(price);
      });

      const selectedCell = tableBody.querySelector(`.discount-table-row[data-pos="${position}"] .table-price-b2b`);
      const percent = Number.parseFloat(selectedCell?.dataset.off) || 0;
      return { amount: total - (total * percent / 100), percent };
    }

    calculate({ validate = false, focusError = false } = {}) {
      const { cuts, errors, firstInvalid } = this.readRows({ validate });
      if (validate && errors.length) {
        this.showMessage(errors[0]);
        if (focusError && firstInvalid) {
          firstInvalid.scrollIntoView({ behavior: 'smooth', block: 'center' });
          firstInvalid.focus({ preventScroll: true });
        }
        return null;
      }

      this.hideMessage();
      const unitPrice = this.getUnitPrice();
      let materialTotal = 0;
      let cutCharge = 0;
      let totalPieces = 0;

      const calculatedCuts = cuts.map((cut) => {
        if (!Number.isFinite(cut.width) || cut.width <= 0 || !Number.isFinite(cut.length) || cut.length <= 0 || !Number.isInteger(cut.pieces) || cut.pieces < 1) {
          return { ...cut, billableWidth: 0, billableLength: 0, squareFeetPerPiece: 0, materialPrice: 0, cutRate: 0, cutCharge: 0, rowTotal: 0 };
        }

        const billableWidth = this.roundUpToSet(cut.width, this.config.maxWidth);
        const billableLength = this.roundUpToSet(cut.length, this.config.maxLength);
        const squareFeetPerPiece = (billableWidth * billableLength) / 144;
        const materialPrice = unitPrice * squareFeetPerPiece * cut.pieces;
        const cutRate = this.getCutRate(cut.pieces);
        const rowCutCharge = cutRate * cut.pieces;
        const rowTotal = materialPrice + rowCutCharge;

        materialTotal += materialPrice;
        cutCharge += rowCutCharge;
        totalPieces += cut.pieces;

        return { ...cut, billableWidth, billableLength, squareFeetPerPiece, materialPrice, cutRate, cutCharge: rowCutCharge, rowTotal };
      });

      const subtotal = materialTotal + cutCharge;
      const discounted = this.getDiscount(subtotal);
      const finalTotal = discounted.amount;

      const result = { unitPrice, cuts: calculatedCuts, materialTotal, cutCharge, subtotal, discountPercent: discounted.percent, finalTotal, totalPieces };
      this.lastResult = result;
      this.updateDisplay(result);
      this.updateProperties(result);
      return result;
    }

    updateDisplay(result) {
      if (this.materialElement) this.materialElement.textContent = this.formatMoney(result.materialTotal);
      if (this.cutChargeElement) this.cutChargeElement.textContent = this.formatMoney(result.cutCharge);
      if (this.totalElement) this.totalElement.textContent = this.formatMoney(result.finalTotal);
      if (this.mainTotalElement) {
        this.mainTotalElement.textContent = this.formatMoney(result.finalTotal);
        this.mainTotalElement.dataset.total = result.finalTotal.toFixed(2);
      }

      const breakdown = this.builder.querySelector('[data-cut-breakdown-v2]');
      if (breakdown) breakdown.hidden = this.builder.dataset.showBreakdown === 'false';
    }

    updateProperties(result) {
      const set = (selector, value) => {
        const input = this.form.querySelector(selector);
        if (input) input.value = value;
      };

      const publicDetails = result.cuts.map((cut) =>
        `Cut ${cut.number}: ${this.cleanNumber(cut.width)}" W x ${this.cleanNumber(cut.length)}" L, ${cut.pieces} piece${cut.pieces === 1 ? '' : 's'}, ${cut.tolerance}`
      ).join(' | ');

      const data = result.cuts.map((cut) => ({
        number: cut.number,
        width: cut.width,
        length: cut.length,
        pieces: cut.pieces,
        tolerance: cut.tolerance,
        billableWidth: cut.billableWidth,
        billableLength: cut.billableLength,
        cutRate: Number(cut.cutRate.toFixed(2)),
      }));

      set('[data-v2-prop-total]', result.finalTotal.toFixed(2));
      set('[data-v2-prop-cut-charge]', result.cutCharge.toFixed(2));
      set('[data-v2-prop-cut-data]', JSON.stringify(data));
      set('[data-v2-prop-cut-details]', publicDetails);
      set('[data-v2-prop-cut-count]', String(result.cuts.length));
      set('[data-v2-prop-total-pieces]', String(result.totalPieces));
      set('[data-v2-prop-material-price]', result.materialTotal.toFixed(2));

      if (this.propertiesContainer) {
        this.propertiesContainer.innerHTML = '';
        const addProperty = (name, value) => {
          const input = document.createElement('input');
          input.type = 'hidden';
          input.name = `properties[${name}]`;
          input.value = value;
          this.propertiesContainer.appendChild(input);
        };

        result.cuts.forEach((cut) => {
          addProperty(`Cut ${cut.number} Details`, `${this.cleanNumber(cut.width)}" W x ${this.cleanNumber(cut.length)}" L`);
          addProperty(`Cut ${cut.number} Pieces`, String(cut.pieces));
          addProperty(`Cut ${cut.number} Tolerance`, cut.tolerance);
        });
      }
    }

    cleanNumber(value) {
      return Number.isFinite(value) ? String(Number(value.toFixed(3))) : '';
    }

    showMessage(message) {
      if (!this.messageElement) return;
      this.messageElement.textContent = message;
      this.messageElement.hidden = false;
    }

    hideMessage() {
      if (!this.messageElement) return;
      this.messageElement.textContent = '';
      this.messageElement.hidden = true;
    }

    formatMoney(amount) {
      try {
        if (window.Shopify && typeof Shopify.formatMoney === 'function') {
          const format = window.theme?.moneyFormat || '${{amount}}';
          return Shopify.formatMoney(Math.round((amount || 0) * 100), format);
        }
      } catch (_) {}

      return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
        style: 'currency',
        currency: window.Shopify?.currency?.active || 'USD',
      }).format(amount || 0);
    }

    destroy() {
      this.unsubscribeVariant?.();
      this.priceObserver?.disconnect();
    }
  }

  const initialize = (scope = document) => {
    scope.querySelectorAll(SELECTOR).forEach((builder) => {
      if (builder.dataset.initialized === 'true') return;
      builder.customCutCalculatorV2 = new CustomCutCalculatorV2(builder);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialize());
  } else {
    initialize();
  }

  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll(SELECTOR).forEach((builder) => builder.customCutCalculatorV2?.destroy());
  });
})();
